/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import {
  COMPANY_CARD_STATUS,
  IGeneralDetailsProps
} from "../../../../../../shared";
import { useStyles } from "../../../../styles";
import DetailsCard from "../DetailsCard";
import DetailsCardItemText from "../DetailsCardItemText";
import DetailsCardItemTitle from "../DetailsCardItemTitle";

const CompaniesGeneralDetails = ({ detailsData }: IGeneralDetailsProps) => {
  const { t } = useTranslation();
  const {
    paperCardItem,
    paperCardItemAlignStart,
    paperCardItemFixedHeight,
    customInfoTooltip
  } = useStyles();

  const getCorrectString = (
    data: string | number | null | undefined,
    translationText: string
  ) => {
    return data === null
      ? null
      : `(${data} ${t(translationText).toLowerCase()})`;
  };

  const infoTooltipItemsArray = [
    "automaticDriverRegistrationEnabled",
    "autoAssignGlobalScheduleToDriver",
    "autoAssignGlobalScheduleToVehicle"
  ];

  const generateTooltipTextColor = (enabled: boolean) =>
    enabled ? "unset" : colors.textDisabled;

  const generateInfoTooltipText = () => (
    <div css={css(customInfoTooltip)}>
      {infoTooltipItemsArray.map((item: string) => (
        <Typography
          variant="body2Regular"
          key={item}
          sx={{
            color: generateTooltipTextColor(
              detailsData
                ? (detailsData[item as keyof typeof detailsData] as boolean)
                : false
            )
          }}
        >
          {t(`Details##${item}`)}
        </Typography>
      ))}
    </div>
  );

  return (
    <DetailsCard
      cardTitle={t("Details##general")}
      infoIcon
      infoTooltip={generateInfoTooltipText()}
    >
      <div css={css([paperCardItem, paperCardItemAlignStart])}>
        <DetailsCardItemTitle title={t("Details##parent company")} />
        <DetailsCardItemText textToRender={detailsData?.parentCompanyName} />
      </div>

      {/* do not show client code if it is undefined */}
      {detailsData?.clientCode && (
        <div css={css([paperCardItem, paperCardItemAlignStart])}>
          <DetailsCardItemTitle title={t("Details##client code")} />
          <DetailsCardItemText textToRender={detailsData?.clientCode} />
        </div>
      )}

      {detailsData?.parentCompanyId === null && (
        <div css={css(paperCardItem)}>
          <DetailsCardItemTitle title={t("Details##credits")} />
          <DetailsCardItemText
            textToRender={detailsData?.creditsCount}
            titleInlineAddition={getCorrectString(
              detailsData?.reservedCreditsCount,
              "Details##reserved"
            )}
          />
        </div>
      )}
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle title={t("Details##vehicles")} />
        <DetailsCardItemText
          textToRender={detailsData?.allVehiclesCount}
          titleInlineAddition={getCorrectString(
            detailsData?.disabledVehiclesCount,
            `Details##${COMPANY_CARD_STATUS.Deactivated.toLowerCase()}`
          )}
        />
      </div>
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle title={t("Details##drivers")} />
        <DetailsCardItemText textToRender={detailsData?.driversCount} />
      </div>
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle title={t("Details##users")} />
        <DetailsCardItemText textToRender={detailsData?.usersCount} />
      </div>

      <div css={css([paperCardItem, paperCardItemFixedHeight])}>
        <DetailsCardItemTitle title={t("Details##api company id")} />
        <DetailsCardItemText
          textToRender={detailsData?.id as string}
          hasCopyButton
          copiedValue={detailsData?.id as string}
        />
      </div>
    </DetailsCard>
  );
};

export default CompaniesGeneralDetails;
